import type { Document, Folder } from "@/types";

/**
 * Local write applied before the server has seen it.
 *
 * Version stays put on purpose. Bumping it here would make the outbox send a
 * number the server never issued, and a later sync would look like a conflict
 * with ourselves — or worse, skip the compare-and-swap entirely.
 */
export function applyLocalDocumentWrite(
  current: Document,
  updates: Record<string, unknown>,
): Document {
  const version = current.version;
  const next: Document = {
    ...current,
    ...updates,
    id: current.id,
    updated_at: new Date().toISOString(),
  };
  next.version = version;
  return next;
}

/**
 * A 409 means the server moved on. The local copy is the author's Saturday
 * night; the server copy is whoever saved first. Combining them is a human
 * decision. Silently taking either side is how sermons disappear.
 */
export function shouldReplaceLocalWithServer(_local: Document, _server: Document): false {
  return false;
}

export function parseApiPath(endpoint: string): { path: string; search: string } {
  const url = endpoint.startsWith("http")
    ? new URL(endpoint)
    : new URL(endpoint, "https://sermonspace.local");
  return { path: url.pathname, search: url.search };
}

function folderBranchIds(folders: Folder[], rootId: number): Set<number> {
  const ids = new Set<number>([rootId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const folder of folders) {
      if (folder.parent_id != null && ids.has(folder.parent_id) && !ids.has(folder.id)) {
        ids.add(folder.id);
        changed = true;
      }
    }
  }
  return ids;
}

/** Reproduce the library list filters against the local mirror. */
export function filterCachedDocuments(
  documents: Document[],
  search: string,
  folders: Folder[] = [],
): Document[] {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const folderParam = params.get("folder");
  const type = params.get("type");
  const archived = params.get("archived") === "true";

  let branch: Set<number> | null = null;
  if (folderParam && folderParam !== "unfiled") {
    const folderId = Number(folderParam);
    if (!Number.isNaN(folderId)) branch = folderBranchIds(folders, folderId);
  }

  return documents
    .filter((document) => Boolean(document.is_archived) === archived)
    .filter((document) => {
      if (folderParam === "unfiled") return document.folder_id == null;
      if (branch) return document.folder_id != null && branch.has(document.folder_id);
      return true;
    })
    .filter((document) => {
      if (!type) return true;
      return document.doc_type === type || String(document.doc_type_id) === type;
    })
    .sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1));
}

export function localSearchNeedle(query: string): string {
  return query.trim().toLowerCase();
}

export function textMatches(haystack: string | null | undefined, needle: string): boolean {
  if (!needle) return false;
  return (haystack || "").toLowerCase().includes(needle);
}
